import clsx from "clsx";
import React, { useEffect } from "react";

// Relative imports (not the "@appsmith/wds" barrel) keep this component out
// of the package-barrel dependency cycle counted by the CI cyclic-deps gate.
import { Button } from "../../Button";
import { inputFieldStyles } from "../../Field";
import { fieldErrorStyles } from "../../FieldError";
import { FieldLabel } from "../../FieldLabel";
import { Text } from "../../Text";

import styles from "./styles.module.css";
import type { SignaturePadProps } from "./types";
import { useSignaturePad } from "./useSignaturePad";

/**
 * SignaturePad wraps the `signature_pad` canvas library ENTIRELY — consumers
 * (the widget layer) never import the library — in the Field chrome idiom of
 * Datepicker/ColorPicker.
 *
 * - Drawing state lives in the library/refs (see useSignaturePad): nothing
 *   per-pointermove touches React state; onStrokeEnd fires ONCE per
 *   completed stroke with the PNG data URL, exported at devicePixelRatio
 *   clamped to [1, 2].
 * - `value` is a CLEAR SIGNAL ONLY — it is never rendered into the canvas
 *   (no fromDataURL / image sink); the live canvas is the display.
 * - a11y: the canvas is role="img" with an accessible name derived from the
 *   label; the Clear affordance is a real (keyboard-reachable) DS Button —
 *   the one interaction available to non-pointer users.
 */
export const SignaturePad = (props: SignaturePadProps) => {
  const {
    backgroundColor,
    className,
    clearLabel = "Clear",
    contextualHelp,
    errorMessage,
    isDisabled = false,
    isInvalid,
    isLoading = false,
    isRequired,
    label,
    onClear,
    onStrokeEnd,
    penColor,
    size = "medium",
    value,
  } = props;
  const isInteractive = !isDisabled && !isLoading;
  const { canvasRef, clear, clearIfUnset } = useSignaturePad({
    backgroundColor,
    isInteractive,
    onClear,
    onStrokeEnd,
    penColor,
    value,
  });

  useEffect(
    function applyClearSignal() {
      clearIfUnset(value);
    },
    [clearIfUnset, value],
  );

  return (
    <div
      className={clsx(inputFieldStyles.field, className)}
      data-disabled={isDisabled ? "" : undefined}
      data-field=""
      data-size={size}
    >
      <FieldLabel
        contextualHelp={contextualHelp}
        isDisabled={isDisabled}
        isRequired={isRequired}
      >
        {label}
      </FieldLabel>
      <div
        className={styles.pad}
        data-disabled={isDisabled ? "" : undefined}
        data-invalid={Boolean(isInvalid) ? "" : undefined}
      >
        <canvas
          aria-disabled={isInteractive ? undefined : true}
          aria-label={isNonBlankLabel(label) ? label : "Signature"}
          className={styles.canvas}
          ref={canvasRef}
          role="img"
        />
      </div>
      <div className={styles.footer}>
        <Button
          isDisabled={!isInteractive}
          onPress={clear}
          size="small"
          variant="ghost"
        >
          {clearLabel}
        </Button>
      </div>
      {Boolean(errorMessage) && (
        <div className={fieldErrorStyles.errorText}>
          <Text color="negative" size="caption">
            {errorMessage}
          </Text>
        </div>
      )}
    </div>
  );
};

/** Widget properties can evaluate to "" from a JS binding — an explicit
 * blank check, not just nullish fallback. */
function isNonBlankLabel(label: string | undefined): label is string {
  return typeof label === "string" && label.trim() !== "";
}
